import {
	Button,
	Label,
	Calendar,
	Select,
	SelectTrigger,
	SelectContent,
	SelectValue,
	SelectItem,
	Popover,
	PopoverTrigger,
	PopoverContent,
} from '@/components/ui';
import { ControlledInput, ControlledTextarea } from '@/components/controlled';
import { DEFAULT_FORM_CONFIG } from '@/constants/formConfig.constant';
import { useRequestCreateEvent } from '@/hooks/httpRequest/events';
import { EventTypeEnum, MessageLanguageEnum } from '@/enums';
import { useForm, type UseFormProps, Controller } from 'react-hook-form';
import { useState } from 'react';
import { getApiErrorMessage, notify } from '@/functions';
import {
	EVENT_DEFAULT_AGE_BUFFER_YEARS,
	EVENT_MAX_PRICE,
	EVENT_MIN_PRICE,
	EVENT_DEFAULT_ROUND_MINUTES,
	EVENT_MIN_DURATION_MINUTES
} from '@/constants';
import { CalendarIcon, Calendar as CalendarLucide, AlertCircle } from 'lucide-react';
import { addMinutes, differenceInMinutes, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { LayoutPage } from '@/components/hoc';
import routes from '@/lib/routes';
import ImageUploadInput from '@/pages/marketingPage/pages/PromotionsPage/components/ImageUploadInput';
import type { ICreateEventFormData } from '@/interfaces';

const CreateEventPage = () => {
	const defaultValues: ICreateEventFormData = {
		type: EventTypeEnum.BLIND_DATING,
		bannerImage: {} as FileList,
		title: { fr: '', en: '' },
		description: { fr: '', en: '' },
		startsAt: undefined,
		endsAt: undefined,
		roundDurationMinutes: EVENT_DEFAULT_ROUND_MINUTES,
		ageBufferYears: EVENT_DEFAULT_AGE_BUFFER_YEARS,
		maxParticipants: '',
		price: '',
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		getValues,
		setValue,
		formState: { isSubmitting, errors }
	} = useForm<ICreateEventFormData>({
		...(DEFAULT_FORM_CONFIG as UseFormProps<ICreateEventFormData>),
		defaultValues
	});

	const watchStartsAt = watch('startsAt');

	/* An event lasts at least 1 hour: the end follows the start when it is empty or too close */
	const keepEndAfter = (start: Date) => {
		const earliestEnd = addMinutes(start, EVENT_MIN_DURATION_MINUTES);
		const end = getValues('endsAt');
		if (!end || end < earliestEnd) setValue('endsAt', earliestEnd, { shouldValidate: !!end });
	};

	const [descLang, setDescLang] = useState<MessageLanguageEnum>(MessageLanguageEnum.FR);

	const hasLangError =
		descLang === MessageLanguageEnum.FR
			? !!(errors.title?.fr || errors.description?.fr)
			: !!(errors.title?.en || errors.description?.en);

	const { triggerCreateEvent } = useRequestCreateEvent({
		onSuccess: () => {
			notify('Événement créé.');
			reset(defaultValues);
			window.close();
		}
	});

	const onSubmit = handleSubmit(async data => {
		try {
			const file = data.bannerImage?.[0];
			if (!file) return;

			await triggerCreateEvent({
				type: data.type,
				bannerImage: file,
				title: data.title,
				description: data.description,
				startsAt: data.startsAt!.toISOString(),
				endsAt: data.endsAt!.toISOString(),
				maxParticipants: data.maxParticipants === '' ? undefined : Number(data.maxParticipants),
				price: data.price === '' ? undefined : Number(data.price),
				config: {
					roundDurationMinutes: Number(data.roundDurationMinutes),
					ageBufferYears: Number(data.ageBufferYears),
				}
			});
		} catch (error) {
			notify(getApiErrorMessage(error, "Erreur lors de la création de l'événement."), 'error');
		}
	}, errs => {
		const hasENError = !!(errs.title?.en || errs.description?.en);
		const hasFRError = !!(errs.title?.fr || errs.description?.fr);
		if (descLang === MessageLanguageEnum.FR && hasENError && !hasFRError) setDescLang(MessageLanguageEnum.EN);
		if (descLang === MessageLanguageEnum.EN && hasFRError && !hasENError) setDescLang(MessageLanguageEnum.FR);
	});

	return (
		<LayoutPage
			header={{
				title: 'Créer un événement',
				description: 'Configurez et programmez un nouvel événement de rencontres en aveugle.'
			}}
			breadcrumb={{
				title: 'Créer un événement',
				subMenus: [
					{
						href: routes.nav.events.getHref(),
						title: 'Événements',
						menus: [{ href: routes.nav.events.getHref(), title: 'Événements' }]
					}
				]
			}}
			icon={CalendarLucide}
		>
			<div className="rounded-xl border bg-card p-6 shadow-xs">
				<form onSubmit={onSubmit} className="flex flex-col gap-6">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
						{/* Configuration Column */}
						<div className="flex flex-col gap-4">
							{/* Language */}
							<div className="flex flex-col items-end gap-1">
								<Select value={descLang} onValueChange={v => setDescLang(v as MessageLanguageEnum)}>
									<SelectTrigger className={cn('w-36 h-8 text-xs', hasLangError && 'border-red-500')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={MessageLanguageEnum.FR}>Français (FR)</SelectItem>
										<SelectItem value={MessageLanguageEnum.EN}>English (EN)</SelectItem>
									</SelectContent>
								</Select>
								{hasLangError && (
									<span className="flex items-center gap-1 text-xs text-red-500">
										<AlertCircle className="h-3 w-3" />
										Champs {descLang === MessageLanguageEnum.FR ? 'FR' : 'EN'} incomplets
									</span>
								)}
							</div>

							{/* Type */}
							<div className="flex flex-col gap-1">
								<Label>Type <span className="text-red-500">*</span></Label>
								<Controller
									name="type"
									control={control}
									rules={{ required: 'Le type est requis' }}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<>
											<Select value={value} onValueChange={onChange}>
												<SelectTrigger className={cn(error && 'border-red-500')}>
													<SelectValue placeholder="Sélectionner un type" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={EventTypeEnum.BLIND_DATING}>Blind Dating</SelectItem>
												</SelectContent>
											</Select>
											{error && <span className="text-xs text-red-500">{error.message}</span>}
										</>
									)}
								/>
							</div>

							{/* Title */}
							<div className="flex flex-col gap-1">
								<Label>
									Titre <span className="text-red-500">*</span>
								</Label>
								<ControlledInput
									name="title.fr"
									control={control}
									placeholder="Titre (FR)"
									className={cn(descLang !== MessageLanguageEnum.FR && 'hidden')}
									rules={{ required: 'Le titre est requis' }}
								/>
								{descLang === MessageLanguageEnum.FR && errors.title?.fr && (
									<span className="text-xs text-red-500">{errors.title.fr.message}</span>
								)}
								<ControlledInput
									name="title.en"
									control={control}
									placeholder="Title (EN)"
									className={cn(descLang !== MessageLanguageEnum.EN && 'hidden')}
									rules={{ required: 'Le titre est requis' }}
								/>
								{descLang === MessageLanguageEnum.EN && errors.title?.en && (
									<span className="text-xs text-red-500">{errors.title.en.message}</span>
								)}
							</div>

							{/* Description */}
							<div className="flex flex-col gap-1">
								<Label>
									Description <span className="text-red-500">*</span>
								</Label>
								<ControlledTextarea
									name="description.fr"
									control={control}
									placeholder="Description (FR)"
									rows={4}
									className={cn('resize-none', descLang !== MessageLanguageEnum.FR && 'hidden')}
									rules={{ required: 'La description est requise' }}
								/>
								{descLang === MessageLanguageEnum.FR && errors.description?.fr && (
									<span className="text-xs text-red-500">{errors.description.fr.message}</span>
								)}
								<ControlledTextarea
									name="description.en"
									control={control}
									placeholder="Description (EN)"
									rows={4}
									className={cn('resize-none', descLang !== MessageLanguageEnum.EN && 'hidden')}
									rules={{ required: 'La description est requise' }}
								/>
								{descLang === MessageLanguageEnum.EN && errors.description?.en && (
									<span className="text-xs text-red-500">{errors.description.en.message}</span>
								)}
							</div>

							{/* Dates */}
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1">
									<Label>
										Début <span className="text-red-500">*</span>
									</Label>
									<Controller
										name="startsAt"
										control={control}
										rules={{
										required: 'Date de début requise',
										validate: v => !v || v > new Date() || 'La date de début doit être dans le futur'
									}}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															type="button"
															variant="outline"
															className={cn(
																'justify-start text-left font-normal text-xs px-3',
																!value && 'text-muted-foreground',
																error && 'border-red-500'
															)}
														>
															<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
															{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Début'}
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={value}
															onSelect={d => {
																if (!d) return onChange(undefined);
																const next = new Date(d);
																if (value) { next.setHours(value.getHours(), value.getMinutes()); }
																onChange(next);
																keepEndAfter(next);
															}}
															autoFocus
															disabled={d => {
																const today = new Date();
																today.setHours(0, 0, 0, 0);
																return d < today;
															}}
														/>
														<div className="border-t p-3">
															<input
																type="time"
																className="w-full rounded-md border px-3 py-1.5 text-sm"
																value={value ? format(value, 'HH:mm') : ''}
																onChange={e => {
																	const [h, m] = e.target.value.split(':').map(Number);
																	const next = value ? new Date(value) : new Date();
																	next.setHours(h, m, 0, 0);
																	onChange(next);
																	keepEndAfter(next);
																}}
															/>
														</div>
													</PopoverContent>
												</Popover>
												{error && <span className="text-xs text-red-500">{error.message}</span>}
											</>
										)}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>
										Fin <span className="text-red-500">*</span>
									</Label>
									<Controller
										name="endsAt"
										control={control}
										rules={{
											required: 'Date de fin requise',
											validate: v =>
												!watchStartsAt ||
												!v ||
												v >= addMinutes(watchStartsAt, EVENT_MIN_DURATION_MINUTES) ||
												"L'événement doit durer au moins 1 heure"
										}}
										render={({ field: { value, onChange }, fieldState: { error } }) => (
											<>
												<Popover>
													<PopoverTrigger asChild>
														<Button
															type="button"
															variant="outline"
															className={cn(
																'justify-start text-left font-normal text-xs px-3',
																!value && 'text-muted-foreground',
																error && 'border-red-500'
															)}
														>
															<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
															{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Fin'}
														</Button>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0">
														<Calendar
															mode="single"
															selected={value}
															onSelect={d => {
																if (!d) return onChange(undefined);
																const next = new Date(d);
																if (value) { next.setHours(value.getHours(), value.getMinutes()); }
																onChange(next);
															}}
															autoFocus
															disabled={d => {
																if (!watchStartsAt) return false;
																const startDay = new Date(watchStartsAt);
																startDay.setHours(0, 0, 0, 0);
																return d < startDay;
															}}
														/>
														<div className="border-t p-3">
															<input
																type="time"
																className="w-full rounded-md border px-3 py-1.5 text-sm"
																value={value ? format(value, 'HH:mm') : ''}
																onChange={e => {
																	const [h, m] = e.target.value.split(':').map(Number);
																	const next = value ? new Date(value) : new Date();
																	next.setHours(h, m, 0, 0);
																	onChange(next);
																}}
															/>
														</div>
													</PopoverContent>
												</Popover>
												{error && <span className="text-xs text-red-500">{error.message}</span>}
											</>
										)}
									/>
								</div>
							</div>

							{/* Rounds */}
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1">
									<Label>
										Durée d'un round (min) <span className="text-red-500">*</span>
									</Label>
									<ControlledInput
										name="roundDurationMinutes"
										control={control}
										type="number"
										placeholder="ex: 5"
										rules={{
											required: 'Champ requis',
											min: { value: 1, message: 'Minimum 1 minute' },
											validate: v => {
												const [start, end] = getValues(['startsAt', 'endsAt']);
												return (
													!start ||
													!end ||
													Number(v) < differenceInMinutes(end, start) ||
													"Le round doit être plus court que l'événement"
												);
											}
										}}
									/>
									{errors.roundDurationMinutes && (
										<span className="text-xs text-red-500">{errors.roundDurationMinutes.message}</span>
									)}
								</div>
								<div className="flex flex-col gap-1">
									<Label>
										Marge d'âge (années) <span className="text-red-500">*</span>
									</Label>
									<ControlledInput
										name="ageBufferYears"
										control={control}
										type="number"
										placeholder="ex: 10"
										rules={{
											required: 'Champ requis',
											min: { value: 0, message: 'Minimum 0' }
										}}
									/>
									{errors.ageBufferYears && (
										<span className="text-xs text-red-500">{errors.ageBufferYears.message}</span>
									)}
								</div>
							</div>
							<p className="-mt-2 text-xs text-muted-foreground">
								La marge élargit le filtre d'âge de chaque participant, de chaque côté, pour trouver plus de monde.
							</p>

							{/* Places and price */}
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1">
									<Label>Nombre de places</Label>
									<ControlledInput
										name="maxParticipants"
										control={control}
										type="number"
										placeholder="Illimité"
										rules={{ min: { value: 2, message: 'Minimum 2 places' } }}
									/>
									{errors.maxParticipants && (
										<span className="text-xs text-red-500">{errors.maxParticipants.message}</span>
									)}
								</div>
								<div className="flex flex-col gap-1">
									<Label>Prix (pièces)</Label>
									<ControlledInput
										name="price"
										control={control}
										type="number"
										placeholder="Gratuit"
										rules={{
											min: { value: EVENT_MIN_PRICE, message: `Minimum ${EVENT_MIN_PRICE} pièces` },
											max: { value: EVENT_MAX_PRICE, message: `Maximum ${EVENT_MAX_PRICE} pièces` },
											validate: v => v === '' || Number.isInteger(Number(v)) || 'Nombre entier requis'
										}}
									/>
									{errors.price && <span className="text-xs text-red-500">{errors.price.message}</span>}
								</div>
							</div>
						</div>

						{/* Image Column */}
						<div className="flex flex-col gap-4 h-fit">
							<div className="flex flex-col gap-1 w-full">
								<Controller
									name="bannerImage"
									control={control}
									rules={{ required: true, validate: v => v?.length > 0 || 'Image requise' }}
									render={({ field: { onChange }, fieldState: { error } }) => (
										<>
											<ImageUploadInput onChange={onChange} error={!!error} aspectRatio="banner" />
											{error && <span className="text-xs text-red-500">{error.message ?? 'Image requise'}</span>}
										</>
									)}
								/>
							</div>
						</div>
					</div>

					<div className="flex justify-end gap-3 pt-2">
						<Button type="button" variant="outline" onClick={() => window.close()} disabled={isSubmitting}>
							Annuler
						</Button>
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? 'Création…' : 'Créer'}
						</Button>
					</div>
				</form>
			</div>
		</LayoutPage>
	);
};

export default CreateEventPage;
